import React, {Component} from 'react';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {ISubscriptionPlan, SubscriptionPlan} from './SubscriptionPlan/SubscriptionPlan';
import s from './SubscriptionPlans.scss';
import {UserInputType} from '../../../constants';
import {get} from 'lodash';
import {ErrorSmall} from '@wix/wix-ui-icons-common/on-stage';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import classNames from 'classnames';
import {SPECS} from '../../../specs';
import {RenderContext, WithRenderContextProps, withRenderContext} from '../../../providers/renderContext';

export const enum SubscriptionPlansDataHooks {
  TITLE = 'plans-title',
  Container = 'subscription-plans-container',
  ErrorMessageContainer = 'error-message-container',
  ErrorMessageText = 'error-message-text',
}

export interface ISubscriptionPlansState {
  plans: ISubscriptionPlan[];
}

export interface ISubscriptionPlansProps
  extends ProvidedGlobalProps,
    IProvidedTranslationProps,
    WithRenderContextProps {
  className?: string;
  showErrors?: boolean;
}

class SubscriptionPlansComponent extends Component<ISubscriptionPlansProps, ISubscriptionPlansState> {
  private readonly subscriptionPlanContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: ISubscriptionPlansProps) {
    const {
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget = UserInputType.SubscriptionPlan === scrollAndFocusInputTarget?.inputType;

      if (isScrollAndFocusInputTarget && this.subscriptionPlanContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.SubscriptionPlan, this.subscriptionPlanContainerRef.current, isQuickView);
      }
    }
  }

  private onItemClicked(item: ISubscriptionPlan) {
    this.handleUserInput(item);
  }

  public handleUserInput = (input: ISubscriptionPlan): void => {
    const {handleUserInput, validateUserInputsChanges} = this.props.globals;
    handleUserInput && handleUserInput(UserInputType.SubscriptionPlan, input, 0);
    validateUserInputsChanges();
  };

  private isSelected(item: ISubscriptionPlan) {
    const subscriptionPlan = get(this.props.globals.userInputs.subscriptionPlan, ['0']);

    if (!subscriptionPlan) {
      return false;
    }

    if (subscriptionPlan.isOneTimePurchase && item.isOneTimePurchase) {
      return true;
    }

    return subscriptionPlan.id === item.id;
  }

  private readonly getErrorMessage = () => {
    const {t} = this.props;
    return (
      <div className={s.errorMessageContainer} data-hook={SubscriptionPlansDataHooks.ErrorMessageContainer}>
        <ErrorSmall className={s.errorIcon} aria-hidden />
        <span data-hook={SubscriptionPlansDataHooks.ErrorMessageText}>
          {t('PRODUCT_PAGE_PLAN_MISSING_ERROR_TOOLTIP')}
        </span>
      </div>
    );
  };

  private readonly handleKeyDown = (event: React.KeyboardEvent) => {
    /* istanbul ignore next */
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    event.preventDefault();

    const {subscriptionPlans} = this.props.globals;
    const currentIndex = subscriptionPlans.findIndex((plan) => this.isSelected(plan));

    const nextIndex =
      event.key === 'ArrowDown'
        ? (currentIndex + 1) % subscriptionPlans.length
        : (currentIndex - 1 + subscriptionPlans.length) % subscriptionPlans.length;

    this.onItemClicked(subscriptionPlans[nextIndex]);
  };

  private readonly isPlanSelected = () => {
    const {subscriptionPlans} = this.props.globals;
    for (const plan of subscriptionPlans) {
      if (this.isSelected(plan)) {
        return true;
      }
    }
    return false;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public render(): JSX.Element {
    const {
      t,
      className,
      showErrors = true,
      renderContext,
      globals: {
        subscriptionPlans,
        subscriptionPlanVariantPrices,
        isProductSubmitted,
        userInputErrors,
        experiments,
        useExperiments,
      },
    } = this.props;

    const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StoresStickyAddToCart);
    const shouldShowErrors = showErrors || !isStickyAddToCartEnabled;
    const hasError = shouldShowErrors && isProductSubmitted && userInputErrors.subscriptionPlan[0];
    const isStickyDrawer = isStickyAddToCartEnabled && renderContext === RenderContext.StickyDrawer;

    return (
      <div
        className={classNames(s.container, className, {
          [s.subscriptionNewDesign]: experiments?.subscriptionPlansNewDesignViewer,
        })}
        data-hook={SubscriptionPlansDataHooks.Container}
        ref={this.subscriptionPlanContainerRef}>
        <fieldset>
          <legend
            data-hook={SubscriptionPlansDataHooks.TITLE}
            className={classNames({[s.stickyDrawer]: isStickyDrawer})}>
            {t('PRODUCT_PAGE_PURCHASE_OPTIONS_TITLE')}
            <span className={s.asterisk} aria-hidden="true">
              *
            </span>
          </legend>
          {subscriptionPlans.map((item, index) => {
            const isSelected = this.isSelected(item);
            const hasSelectedPlan = this.isPlanSelected();
            const shouldBeFocusable = isSelected || (!hasSelectedPlan && index === 0);
            return (
              <SubscriptionPlan
                key={index}
                className={s.planItem}
                plan={item}
                planVariantFormattedPrice={subscriptionPlanVariantPrices?.[item.id]}
                isSelected={this.isSelected(item)}
                onClick={() => {
                  this.onItemClicked(item);
                }}
                hasError={hasError}
                shouldFocus={isSelected}
                tabIndex={shouldBeFocusable ? 0 : -1}
                onKeyDown={this.handleKeyDown}
              />
            );
          })}
          {hasError && this.getErrorMessage()}
        </fieldset>
      </div>
    );
  }
}

export const SubscriptionPlans = withGlobalProps(
  withTranslations('globals.texts')(withRenderContext(SubscriptionPlansComponent))
);
